import React, { useState } from 'react';
import { useCondominium } from '../context/CondominiumContext';
import { Building, Home, X, ChevronDown, MapPin, Plus } from 'lucide-react';

interface PropertyTypeSelectorProps {
  onSelect: (propertyType: string, condominiumId?: string) => void;
  onCancel: () => void;
}

const PropertyTypeSelector: React.FC<PropertyTypeSelectorProps> = ({ onSelect, onCancel }) => {
  const { condominiums } = useCondominium();
  const [selectedType, setSelectedType] = useState('');
  const [step, setStep] = useState<'type' | 'condominium'>('type');
  const [isCondominiumProperty, setIsCondominiumProperty] = useState<boolean | null>(null);
  const [selectedCondominium, setSelectedCondominium] = useState('');

  const propertyTypes = [
    {
      category: 'Residencial',
      icon: Home,
      color: 'bg-blue-500',
      types: [
        { value: 'Residential / Apartment', label: 'Apartamento' },
        { value: 'Residential / Home', label: 'Casa' },
        { value: 'Residential / Condominium', label: 'Casa de Condomínio' },
        { value: 'Residential / Penthouse', label: 'Cobertura' },
        { value: 'Residential / Flat', label: 'Flat' },
        { value: 'Residential / Studio', label: 'Studio' },
        { value: 'Residential / Loft', label: 'Loft' },
        { value: 'Residential / Duplex', label: 'Duplex' },
        { value: 'Residential / Triplex', label: 'Triplex' },
        { value: 'Residential / Quadruplex', label: 'Quadruplex' },
        { value: 'Residential / Village House', label: 'Casa de Vila' },
        { value: 'Residential / Farm', label: 'Chácara' },
        { value: 'Residential / Farm Ranch', label: 'Fazenda' },
        { value: 'Residential / Sobrado', label: 'Sobrado' },
        { value: 'Residential / Land Lot', label: 'Terreno' }
      ]
    },
    {
      category: 'Comercial',
      icon: Building,
      color: 'bg-green-500',
      types: [
        { value: 'Commercial / Building', label: 'Prédio' },
        { value: 'Commercial / Office', label: 'Sala' },
        { value: 'Commercial / Shop', label: 'Loja' },
        { value: 'Commercial / Warehouse', label: 'Galpão' },
        { value: 'Commercial / Industrial Shed', label: 'Barracão' },
        { value: 'Commercial / Business Point', label: 'Ponto Comercial' }
      ]
    }
  ];

  const handleTypeSelect = (typeValue: string) => {
    setSelectedType(typeValue);
    setStep('condominium');
  };

  const handleCondominiumChoice = (hasCondominium: boolean) => {
    setIsCondominiumProperty(hasCondominium);
    
    if (!hasCondominium) {
      // Se não é imóvel de condomínio, chama onSelect sem condominiumId
      onSelect(selectedType);
    } else {
      // Se é imóvel de condomínio, continua para seleção do condomínio
      setSelectedCondominium('');
    }
  };

  const handleCondominiumSelect = () => {
    if (!selectedCondominium) {
      alert('Por favor, selecione um condomínio');
      return;
    }
    onSelect(selectedType, selectedCondominium);
  };

  const handleBack = () => {
    if (step === 'condominium' && isCondominiumProperty === null) {
      setStep('type');
      setSelectedType('');
    } else if (isCondominiumProperty === true) {
      setIsCondominiumProperty(null);
      setSelectedCondominium('');
    } else {
      setStep('type');
      setSelectedType('');
      setIsCondominiumProperty(null);
    }
  };

  const selectedTypeData = propertyTypes
    .flatMap(cat => cat.types)
    .find(type => type.value === selectedType);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">
            {step === 'type' ? 'Selecionar Tipo de Imóvel' : 
             isCondominiumProperty === null ? 'Imóvel de Condomínio?' :
             'Selecionar Condomínio'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {step === 'type' ? (
            // Step 1: Select Property Type
            <div className="space-y-8">
              {propertyTypes.map((category) => {
                const Icon = category.icon;
                return (
                  <div key={category.category} className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <div className={`p-2 rounded-lg ${category.color}`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {category.category}
                      </h3>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {category.types.map((type) => (
                        <button
                          key={type.value}
                          onClick={() => handleTypeSelect(type.value)}
                          className="p-4 border border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors text-left group"
                        >
                          <span className="text-sm font-medium text-gray-800 group-hover:text-orange-600">
                            {type.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : isCondominiumProperty === null ? (
            // Step 2: Choose if it's a condominium property
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Este imóvel fica em um condomínio?
                </h3>
                <p className="text-gray-600 mb-2">
                  Tipo selecionado: <strong>{selectedTypeData?.label}</strong>
                </p>
                <p className="text-sm text-gray-500">
                  Escolha se este imóvel está localizado dentro de um condomínio fechado
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                <button
                  onClick={() => handleCondominiumChoice(true)}
                  className="p-6 border-2 border-gray-200 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-colors group"
                >
                  <div className="text-center">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-orange-200">
                      <Building className="w-8 h-8 text-orange-600" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">
                      Sim, é de condomínio
                    </h4>
                    <p className="text-sm text-gray-600">
                      O imóvel está localizado dentro de um condomínio fechado
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => handleCondominiumChoice(false)}
                  className="p-6 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-colors group"
                >
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200">
                      <Home className="w-8 h-8 text-green-600" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">
                      Não é de condomínio
                    </h4>
                    <p className="text-sm text-gray-600">
                      O imóvel é independente, não fica em condomínio
                    </p>
                  </div>
                </button>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={handleBack}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Voltar
                </button>
              </div>
            </div>
          ) : (
            // Step 3: Select Condominium
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Selecionar Condomínio
                </h3>
                <p className="text-gray-600 mb-2">
                  Tipo: <strong>{selectedTypeData?.label}</strong> • <span className="text-orange-600">Em condomínio</span>
                </p>
                <p className="text-sm text-gray-500">
                  Selecione o condomínio onde o imóvel está localizado
                </p>
              </div>

              {condominiums.length > 0 ? (
                <div className="space-y-4">
                  <div className="relative">
                    <select
                      value={selectedCondominium}
                      onChange={(e) => setSelectedCondominium(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent appearance-none bg-white"
                    >
                      <option value="">Selecione um condomínio...</option>
                      {condominiums
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((condominium) => (
                        <option key={condominium.id} value={condominium.id}>
                          {condominium.name} - {condominium.address.neighborhood}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>

                  {selectedCondominium && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      {(() => {
                        const cond = condominiums.find(c => c.id === selectedCondominium);
                        return cond ? (
                          <div>
                            <h4 className="font-medium text-orange-800 mb-2 flex items-center">
                              <Building className="w-4 h-4 mr-2" />
                              {cond.name}
                            </h4>
                            <p className="text-sm text-orange-700 mb-2 flex items-center">
                              <MapPin className="w-4 h-4 mr-1" />
                              {cond.address.street}, {cond.address.number} - {cond.address.neighborhood}
                            </p>
                            <p className="text-sm text-orange-600">
                              ✨ {cond.features.length} características disponíveis
                            </p>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}

                  <div className="flex justify-between space-x-4">
                    <button
                      onClick={handleBack}
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Voltar
                    </button>
                    <button
                      onClick={handleCondominiumSelect}
                      disabled={!selectedCondominium}
                      className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Continuar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">
                    Nenhum condomínio cadastrado
                  </h4>
                  <p className="text-gray-600 mb-6">
                    Para cadastrar um {selectedTypeData?.label} em condomínio, você precisa primeiro cadastrar um condomínio.
                  </p>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-center space-x-2 mb-2">
                      <Plus className="w-5 h-5 text-blue-600" />
                      <span className="text-blue-800 font-medium">Dica:</span>
                    </div>
                    <p className="text-blue-700 text-sm">
                      Vá para a seção "Condomínios" no dashboard e cadastre primeiro o condomínio onde o imóvel está localizado.
                    </p>
                  </div>

                  <div className="flex justify-center space-x-4">
                    <button
                      onClick={handleBack}
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Voltar
                    </button>
                    <button
                      onClick={() => {
                        alert('Redirecionando para cadastro de condomínios...\n\nVá para: Dashboard > Condomínios > Novo Condomínio');
                        onCancel();
                      }}
                      className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-2"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Cadastrar Condomínio</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyTypeSelector;